import React, { useCallback } from 'react';
import clsx from 'clsx';
import { Field, FieldArray, useField } from 'formik';
import { DatePicker, FormikInput, PhoneInput, SelectInput, UploadFileInput, Checkbox } from "presentation/common";
import GoogleRecaptcha from "../google-recaptcha/GoogleRecaptcha";
import FilesTable from "../files-table/FilesTable";
import { personType, vehicleType, gasTypes } from "application/globals/new-request.data";
import { useNewReportState } from "presentation/new-report/NewReportContext";
import { IsOwnerInput } from "../is-owner-input/is-owner-input";
import { MailIcon, QuestionIcon } from "presentation/common/components/style-guide/icons";
import { PersonForm } from "../person-form/person-form";
import { CommonBillForm } from "presentation/new-report/components/common-bill-form/common-bill-form";
import ReactTooltip from "react-tooltip";
import { IsNotOwner } from "presentation/new-report/NewReportConstant";
import { formatRut } from "presentation/common/helpers/validate/rut/rut";
import styles from './PersonalForm.module.scss';
import { useUploadDocuments } from "./use-upload-documents";
import { useEffect } from 'react';

export const PersonalForm = ({ values = {}, setFieldValue, setFieldTouched = [] }) => {
  const {
    recaptchaRef,
    billIssuerInnerRef,
    dataState: { billIssuerAvailable, managers },
    onChangeBillIssuer,
    userInfo,
  } = useNewReportState();

  const [_, metaFilesField] = useField('files');
  const { onUploadFiles, inputRef } = useUploadDocuments();

  const onDeleteFile = useCallback((file) => {
    setFieldValue('files', values.files.filter(f => f.id !== file.id));
  }, [setFieldValue, values]);

  const handleOptionChange = useCallback((name, item) => {
    setFieldValue(name, item?.value || '');
    setFieldTouched(name, true, false);
  }, [setFieldValue, setFieldTouched]);

  const handleBillIssuerChange = useCallback(item => {
    handleResetOnBillIssuerChange();
    setFieldValue('billIssuerSelected', item);
    handleOptionChange('billIssuer', item);
    onChangeBillIssuer(item);
  }, [setFieldValue, setFieldTouched]);

  const handlePhoneChange = useCallback((v, phoneDetails) => {
    setFieldValue(`ownerBillInfo.phone`, v, true);
    setFieldValue(`ownerBillInfo.countryCode`, phoneDetails.countryCode.toUpperCase());
    setTimeout(() => setFieldTouched(`ownerBillInfo.phone`, true, false));
  }, [setFieldValue]);

  const handleProfileToggle = useCallback(v => {
    const { name, rut, email, firstName, lastName, profile } = userInfo || {};

    if (v.target.checked) {
      setFieldValue('rut', formatRut(rut), true);
      setFieldValue('name', name, true);
      setFieldValue('firstName', firstName, true);
      setFieldValue('lastName', lastName, true);
      setFieldValue('email', email, true);
      setFieldValue('phone', profile?.phone, true);
    }
  }, [values, userInfo]);

  const handleBillIsuerManagerChange = useCallback((manager) => {

    if (manager) {
      const { name, rut, email, second_lastname, lastname, phone } = manager;
      setFieldValue('rut', formatRut(rut), true);
      setFieldValue('name', name, true);
      setFieldValue('firstName', lastname, true);
      setFieldValue('lastName', second_lastname, true);
      setFieldValue('email', email, true);
      setFieldValue('emailConfirm', email, true);
      setFieldValue('phone', phone, true);
    }
  }, [values, managers]);

  const handleResetOnBillIssuerChange = useCallback(() => {
    setFieldValue('rut', "", false);
    setFieldTouched('rut', false, false);
    setFieldValue('manager', "", false);
    setFieldTouched('manager', false, false);
    setFieldValue('name', "", false);
    setFieldTouched('name', false, false);
    setFieldValue('firstName', "", false);
    setFieldTouched('firstName', false, false);
    setFieldValue('lastName', "", false);
    setFieldTouched('lastName', false, false);
    setFieldValue('email', "", false);
    setFieldTouched('email', false, false);
    setFieldValue('emailConfirm', "", false);
    setFieldTouched('emailConfirm', false, false);
    setFieldValue('phone', "", false);
    setFieldTouched('phone', false, false);
  }, [])

  useEffect(() => {
    setFieldValue('managers', managers);
    if (managers.length === 1) {
      setFieldValue('manager', managerLabel(), true);
      handleBillIsuerManagerChange(managers[0]);
    }
  }, [managers])

  const managerLabel = () => {
    if (managers.length > 1) {
      return managers.map(mng => ({ item: mng, value: [mng?.name, mng?.lastname, `(${mng?.rut})`].join(' '), label: [mng?.name, mng?.lastname, `(${mng?.rut})`].join(' ') }));
    }
    const { rut, name, lastname } = managers[0];
    return [name, lastname, `(${rut})`].join(' ');
  }
  const files = values?.files || [];


  return (
    <div>

      <span>
        <div className="my-4">
          <span className={styles.formTitle}>Datos Vehiculares</span>
        </div>
      </span>


      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 gap-y-6 my-4">
        <FormikInput
          required
          Component={SelectInput}
          name="billIssuer"
          label="Emisor de Factura"
          options={billIssuerAvailable}
          onSelect={handleBillIssuerChange}
          innerRef={billIssuerInnerRef}
          appendToBody
        />
        <FormikInput
          required
          Component={DatePicker}
          name="dateOfIssue"
          label="Emisión de Factura"
          max={new Date()}
        />

        <FormikInput
          required
          label="Monto Neto"
          name="netAmount"
        />

        <FormikInput
          required
          Component={SelectInput}
          name="vehicleType"
          label="Tipo de Vehículo"
          options={vehicleType}
          onSelect={item => handleOptionChange('vehicleType', item)}
          appendToBody
        />

        <FormikInput
          required
          Component={SelectInput}
          name="fuel_type"
          label="Tipo de Combustible"
          options={gasTypes}
          onSelect={item => handleOptionChange('fuel_type', item)}
          appendToBody
        />

        <FormikInput
          required
          label="Año"
          type='number'
          name="anual"
        />



      </div>

      {values.billIssuer && <>
        <span>
          <div className="my-4">
            <span className={styles.formTitle + " mr-4"}>Datos Personales</span>

            {
              (userInfo && managers.length === 0) &&
              (
                <span className={styles.pickProfileData}>
                  <Checkbox
                    value={true} id="profileCheck"
                    onChange={handleProfileToggle}
                    label="Usar los datos de Mi Perfil" />
                </span>
              )
            }
          </div>
        </span>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 gap-y-6">
          {managers.length === 1 && <FormikInput
            name='manager'
            label="Nombre y Apellidos"
            disabled
            required />}
          {managers.length > 1 && <FormikInput
            required
            Component={SelectInput}
            name='manager'
            label="Nombre y Apellidos"
            options={managerLabel()}
            onSelect={v => {
              handleBillIsuerManagerChange(v?.item)
              handleOptionChange('manager', v)
            }}
            appendToBody
          />}

          {managers.length === 0 && <>
            <PersonForm setFieldTouched={setFieldTouched} setFieldValue={setFieldValue} />
            {
              !userInfo &&
              <FormikInput
                required
                prefixIcon={(<MailIcon fill="#9FA6B2" />)}
                label="Confirmación de Correo"
                name="emailConfirm"
              />
            }
          </>}



          <FormikInput
            required
            Component={IsOwnerInput}
            name="isOwner"
            label="¿Es propietario del vehículo?"
          />
        </div>

        {
          values.isOwner === IsNotOwner &&
          <>
            <ReactTooltip
              id='tooltip-info' type='info'
              effect='solid' backgroundColor="white" border
              borderColor='var(--primary)' textColor='var(--primary)'>
              Debe insertarse la información del primer propietario en caso de pertenecer a una comunidad.
            </ReactTooltip>

            <span>
              <div className="my-4 flex gap-1 items-center">
                <span className={styles.formTitle}>Datos Propietario</span>
                <QuestionIcon fontSize='xSmall' data-tip data-for='tooltip-info' />

              </div>
            </span>
            <span>

            </span>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 gap-y-6">
              <FormikInput
                required
                Component={SelectInput}
                options={personType}
                name="personType"
                label="Tipo de Persona"
              />

              {
                values.personType === 'NATURAL'
                && <PersonForm
                  name='personInfo.'
                  setFieldTouched={setFieldTouched}
                  setFieldValue={setFieldValue} />
              }
              {
                values.personType === 'JURÍDICA'
                &&
                <>
                  <CommonBillForm
                    isRequiredEconomicActivity={false}
                    name='ownerBillInfo'
                    setFieldTouched={setFieldTouched}
                    setFieldValue={setFieldValue} />

                  <FormikInput
                    Component={PhoneInput}
                    country={'cl'}
                    name={`ownerBillInfo.phone`}
                    label="Teléfono"
                    required
                    onChange={handlePhoneChange}
                  />

                  <FormikInput
                    required
                    prefixIcon={(<MailIcon fill="#9FA6B2" />)}
                    label="Correo Electrónico"
                    name={`ownerBillInfo.email`}
                  />
                </>
              }
            </div>
          </>
        }

      </>}


      <ReactTooltip
        id='download-tooltip-info'
        type='info'
        effect='solid'
        backgroundColor="white"
        border
        borderColor='var(--primary)'
        textColor='var(--primary)'>
        <div style={{ maxWidth: '270px' }}>
          <p>
            Debe adjuntar una factura y si es un "CHASIS CABINADO" o "CAMION" debe agregar además el DS55.
          </p>
        </div>
      </ReactTooltip>

      <div className="flex justify-center flex-col mb-2 w-full mt-4">
        <label className="flex gap-2 items-center mb-2" style={{ color: '#374151', lineHeight: 0 }}>
          <span className="text-red-600">*</span> Documento(s)
          <QuestionIcon fontSize='xSmall' data-tip data-for='download-tooltip-info' />
        </label>

        <div className="flex w-max">
          <FieldArray
            name="files"
            render={arrayHelpers => (
              <UploadFileInput
                required
                accept="application/pdf"
                multiple
                ref={inputRef}
                onChange={(v) => onUploadFiles(arrayHelpers, v.currentTarget.files)} />
            )} />
        </div>

        {metaFilesField.touched && metaFilesField.error && <p
          className="mt-2 text-sm text-red-600">{Array.isArray(metaFilesField.error) ? metaFilesField.error.find(error => error?.type)?.type : metaFilesField.error}</p>}
      </div>

      {
        files.length > 0 &&
        <FilesTable files={files} onDeleteFile={onDeleteFile} handleOptionChange={handleOptionChange} />
      }
      {
        !userInfo &&
        <div className="col-sm">
          <Field name="recaptcha">
            {({ meta }) => (
              <>
                <GoogleRecaptcha ref={recaptchaRef} onChange={(e) => {
                  setFieldValue('recaptcha', e || '');
                }} />
                {meta.touched && meta.error &&
                  <small className={clsx('text-sm text-red-600')}>{meta.error}</small>}
              </>
            )}
          </Field>
        </div>}
    </div>
  );
};

export default PersonalForm;
